// Trading UI Constants

export const CHART_COLORS = {
  bullish: '#16A34A',    // green-600
  bearish: '#EF4444',    // red-500
  volume: 'rgba(37, 99, 235, 0.2)',  // blue-600 with alpha
  grid: '#E5E7EB',       // gray-200
  crosshair: '#9CA3AF',  // gray-400
  sma20: '#2563EB',      // blue-600
  sma50: '#EF4444',      // red-500
  ema20: '#16A34A',      // green-600
  rsi: '#7C3AED',        // purple-600
  macd: '#2563EB',       // blue-600
  macdSignal: '#EF4444', // red-500
  macdHist: 'rgba(37, 99, 235, 0.3)',
  bb: { upper: '#2563EB', lower: '#2563EB', fill: 'rgba(37, 99, 235, 0.05)' },
  vwap: '#D97706',       // amber-600
  supertrend: { up: '#16A34A', down: '#EF4444' },
};

export const TIMEFRAMES = [
  { key: 'ONE_MINUTE', label: '1m', days: 5 },
  { key: 'THREE_MINUTE', label: '3m', days: 10 },
  { key: 'FIVE_MINUTE', label: '5m', days: 25 },
  { key: 'FIFTEEN_MINUTE', label: '15m', days: 60 },
  { key: 'THIRTY_MINUTE', label: '30m', days: 90 },
  { key: 'ONE_HOUR', label: '1H', days: 180 },
  { key: 'ONE_DAY', label: '1D', days: 365 },
];

export const INDICATORS = [
  { key: 'sma', label: 'SMA', params: { period: 20 }, overlay: true },
  { key: 'ema', label: 'EMA', params: { period: 20 }, overlay: true },
  { key: 'bb', label: 'Bollinger Bands', params: { period: 20, stdDev: 2 }, overlay: true },
  { key: 'vwap', label: 'VWAP', params: {}, overlay: true },
  { key: 'supertrend', label: 'SuperTrend', params: { period: 10, multiplier: 3 }, overlay: true },
  { key: 'rsi', label: 'RSI', params: { period: 14 }, overlay: false },
  { key: 'macd', label: 'MACD', params: { fast: 12, slow: 26, signal: 9 }, overlay: false },
];

export const OPTION_STRATEGY_TEMPLATES = [
  { name: 'Long Straddle', legs: [{ type: 'BUY', option: 'CE' }, { type: 'BUY', option: 'PE' }] },
  { name: 'Short Straddle', legs: [{ type: 'SELL', option: 'CE' }, { type: 'SELL', option: 'PE' }] },
  { name: 'Bull Call Spread', legs: [{ type: 'BUY', option: 'CE', offset: 0 }, { type: 'SELL', option: 'CE', offset: 1 }] },
  { name: 'Bear Put Spread', legs: [{ type: 'BUY', option: 'PE', offset: 0 }, { type: 'SELL', option: 'PE', offset: -1 }] },
  { name: 'Iron Condor', legs: [
    { type: 'SELL', option: 'PE', offset: -1 }, { type: 'BUY', option: 'PE', offset: -2 },
    { type: 'SELL', option: 'CE', offset: 1 }, { type: 'BUY', option: 'CE', offset: 2 },
  ]},
  { name: 'Iron Butterfly', legs: [
    { type: 'BUY', option: 'PE', offset: -1 }, { type: 'SELL', option: 'PE', offset: 0 },
    { type: 'SELL', option: 'CE', offset: 0 }, { type: 'BUY', option: 'CE', offset: 1 },
  ]},
];

export const ALGO_CONDITIONS = {
  price: ['crosses above', 'crosses below', 'is above', 'is below'],
  indicator: ['crosses above', 'crosses below', 'is above', 'is below', 'is between'],
  volume: ['is above', 'is below', 'spikes above'],
  time: ['is after', 'is before', 'is between'],
};

export const ALGO_ACTIONS = ['BUY', 'SELL', 'EXIT_LONG', 'EXIT_SHORT', 'ALERT'];

export const P_AND_L_COLORS = {
  profit: { text: 'text-mint-400', bg: 'bg-mint-500/10', border: 'border-mint-500/20' },
  loss: { text: 'text-gold-400', bg: 'bg-gold-500/10', border: 'border-gold-500/20' },
  neutral: { text: 'text-frost-500', bg: 'bg-white/[0.03]', border: 'border-white/[0.06]' },
};

export const OVERLAY_COLORS = {
  entry: '#16A34A', stopLoss: '#EF4444', target: '#2563EB',
  orbHigh: '#D97706', orbLow: '#D97706',
  expectedUpper: '#7C3AED', expectedLower: '#7C3AED',
  prevDayHigh: '#6B7280', prevDayLow: '#6B7280',
  weeklyHigh: '#0891B2', weeklyLow: '#0891B2',
};

export const OVERLAY_STYLES = {
  entry:         { lineWidth: 2, lineStyle: 0, axisLabelVisible: true },
  stopLoss:      { lineWidth: 2, lineStyle: 0, axisLabelVisible: true },
  target:        { lineWidth: 2, lineStyle: 0, axisLabelVisible: true },
  orbHigh:       { lineWidth: 1, lineStyle: 1, axisLabelVisible: true },
  orbLow:        { lineWidth: 1, lineStyle: 1, axisLabelVisible: true },
  expectedUpper: { lineWidth: 1, lineStyle: 1, axisLabelVisible: true },
  expectedLower: { lineWidth: 1, lineStyle: 1, axisLabelVisible: true },
  prevDayHigh:   { lineWidth: 1, lineStyle: 2, axisLabelVisible: false },
  prevDayLow:    { lineWidth: 1, lineStyle: 2, axisLabelVisible: false },
  weeklyHigh:    { lineWidth: 1, lineStyle: 2, axisLabelVisible: false },
  weeklyLow:     { lineWidth: 1, lineStyle: 2, axisLabelVisible: false },
};

export const MARKER_COLORS = {
  buy:  { color: '#16A34A', shape: 'arrowUp',   position: 'belowBar' },
  sell: { color: '#EF4444', shape: 'arrowDown', position: 'aboveBar' },
};

export const REGIME_CHART_COLORS = {
  LOW_VOL: '#3B82F6', NORMAL: '#10B981', HIGH_VOL: '#F59E0B', EXPANSION: '#EF4444',
};

export const TIMEFRAME_SECONDS = {
  ONE_MINUTE: 60, THREE_MINUTE: 180, FIVE_MINUTE: 300,
  FIFTEEN_MINUTE: 900, THIRTY_MINUTE: 1800, ONE_HOUR: 3600, ONE_DAY: 86400,
};
